import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { normalizeExtension, normalizeMobile } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { ApiError, errorMessage } from '../../../lib/api';
import { checkExtension, usePatchProfile, type ProfilePatch } from '../../onboarding/user/api';
import type { Profile, ProfileField, ProfileSource } from '../../onboarding/user/types';
import styles from './ProfileFields.module.css';

/**
 * Profile fields shared by the wizard step Profil (Onboarding Nutzer 02/03, M2) and Mein Profil (Einstellungen
 * 01–03, M2): the eyebrow-labelled inputs of the frames, the client checks with the server's sentences, the live
 * extension check and the per-field save (`PATCH /settings/profile`).
 */

export type FieldDensity = 'wizard' | 'settings' | 'mobile';

export interface ProfileInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label: string;
  /** „Pflichtfeld", „ungültig", „Hinweis" — appended to the label in the error/warning state. */
  stateLabel?: string | null | undefined;
  tone?: 'error' | 'warning' | null | undefined;
  message?: ReactNode;
  density: FieldDensity;
  /** Right-aligned note inside a read-only field („Login-Adresse"). */
  note?: string | undefined;
  prefix?: string | undefined;
  suggestions?: readonly string[] | undefined;
}

/** One field of the frames: eyebrow label (10.5 px, uppercase), well input, message line in the state colour. */
export function ProfileInput({
  label,
  stateLabel,
  tone,
  message,
  density,
  note,
  prefix,
  suggestions,
  readOnly,
  className,
  id,
  ...rest
}: ProfileInputProps) {
  const generated = useId();
  const inputId = id ?? generated;
  const messageId = `${inputId}-message`;
  const listId = suggestions && suggestions.length > 0 ? `${inputId}-list` : undefined;
  return (
    <div className={clsx(styles.field, density === 'mobile' && styles.mobileField, className)}>
      <label
        htmlFor={inputId}
        className={clsx(
          styles.label,
          tone === 'error' && styles.labelError,
          tone === 'warning' && styles.labelWarning,
        )}
      >
        {stateLabel ? `${label} · ${stateLabel}` : label}
      </label>
      <div
        className={clsx(
          styles.control,
          styles[density],
          readOnly && styles.readOnly,
          tone === 'error' && styles.controlError,
          tone === 'warning' && styles.controlWarning,
        )}
      >
        {prefix ? <span className={styles.prefix}>{prefix}</span> : null}
        <input
          id={inputId}
          className={styles.input}
          readOnly={readOnly}
          aria-invalid={tone === 'error' ? true : undefined}
          aria-describedby={message ? messageId : undefined}
          list={listId}
          {...rest}
        />
        {note ? <span className={styles.note}>{note}</span> : null}
      </div>
      {listId ? (
        <datalist id={listId}>
          {suggestions?.map((value) => (
            <option key={value} value={value} />
          ))}
        </datalist>
      ) : null}
      {message ? (
        <span
          id={messageId}
          className={clsx(styles.message, tone === 'warning' ? styles.messageWarning : styles.messageError)}
        >
          {message}
        </span>
      ) : null}
    </div>
  );
}

export const PROFILE_FIELDS: readonly ProfileField[] = [
  'firstName',
  'lastName',
  'jobTitle',
  'extension',
  'mobilePhone',
  'location',
  'googleMeetUrl',
  'teamsUrl',
  'zoomUrl',
];

/** The personal static meeting links of the card „Videokonferenz-Links" (Mein Profil). */
export const VIDEO_LINK_FIELDS = [
  'googleMeetUrl',
  'teamsUrl',
  'zoomUrl',
] as const satisfies readonly ProfileField[];

export const FIELD_LABELS: Record<ProfileField, string> = {
  firstName: 'Vorname',
  lastName: 'Nachname',
  jobTitle: 'Funktion',
  extension: 'Durchwahl',
  mobilePhone: 'Mobil',
  location: 'Standort',
  googleMeetUrl: 'Google Meet',
  teamsUrl: 'Microsoft Teams',
  zoomUrl: 'Zoom',
};

const MESSAGES = {
  firstName: 'Bitte geben Sie Ihren Vornamen an.',
  lastName: 'Bitte geben Sie Ihren Nachnamen an.',
  mobileWizard: 'Format +49 … erwartet.',
  mobileSettings: 'Bitte geben Sie eine vollständige Rufnummer im Format +49 … an.',
  extension: 'Bitte geben Sie die Durchwahl als Ziffern an, z. B. -214.',
  videoLink: 'Bitte einen gültigen https-Link eingeben.',
} as const;

/** The server's rule for a meeting link: empty, or an https URL with a host (≤ 500 characters). */
function isMeetingLink(value: string): boolean {
  if (value.length > 500 || !value.startsWith('https://')) return false;
  try {
    return new URL(value).hostname !== '';
  } catch {
    return false;
  }
}

export type ProfileValues = Record<ProfileField, string>;

export function valuesOf(profile: Profile): ProfileValues {
  return {
    firstName: profile.firstName,
    lastName: profile.lastName,
    jobTitle: profile.jobTitle ?? '',
    extension: profile.extension ?? '',
    mobilePhone: profile.mobilePhone ?? '',
    location: profile.location ?? '',
    googleMeetUrl: profile.googleMeetUrl ?? '',
    teamsUrl: profile.teamsUrl ?? '',
    zoomUrl: profile.zoomUrl ?? '',
  };
}

/** Client check with the server's sentences; null when the value can be sent. */
export function validateField(field: ProfileField, value: string, source: ProfileSource): string | null {
  const trimmed = value.trim();
  switch (field) {
    case 'firstName':
      return trimmed === '' ? MESSAGES.firstName : null;
    case 'lastName':
      return trimmed === '' ? MESSAGES.lastName : null;
    case 'mobilePhone':
      if (trimmed === '') return null;
      return normalizeMobile(trimmed) === null
        ? source === 'wizard'
          ? MESSAGES.mobileWizard
          : MESSAGES.mobileSettings
        : null;
    case 'extension':
      if (trimmed === '') return null;
      return normalizeExtension(trimmed) === null ? MESSAGES.extension : null;
    case 'googleMeetUrl':
    case 'teamsUrl':
    case 'zoomUrl':
      return trimmed === '' || isMeetingLink(trimmed) ? null : MESSAGES.videoLink;
    case 'jobTitle':
    case 'location':
      return null;
  }
}

function patchValue(field: ProfileField, value: string): string | null {
  const trimmed = value.trim();
  if (field === 'firstName' || field === 'lastName') return trimmed;
  return trimmed === '' ? null : trimmed;
}

/** The extension held by another member: message of the source's context and the free suggestion. */
export interface ExtensionConflict {
  message: string;
  suggestion: string | null;
}

export interface ProfileForm {
  values: ProfileValues;
  errors: Partial<Record<ProfileField, string>>;
  conflict: ExtensionConflict | null;
  /** Fields with an error plus the extension conflict („Bitte korrigieren Sie drei Felder …"). */
  problemCount: number;
  /** A save was refused (Einstellungen 03 banner and bar). */
  failed: boolean;
  saving: boolean;
  setValue: (field: ProfileField, value: string) => void;
  /** Saves one field when it changed (blur); resolves true when it is stored. */
  commit: (field: ProfileField) => Promise<boolean>;
  /** Saves every changed field; resolves true when everything is stored. */
  commitAll: () => Promise<boolean>;
  /** Back to the stored profile („Änderungen verwerfen"). */
  reset: () => void;
  takeSuggestion: () => void;
}

/** Editing state of the profile fields; `onSaved` runs after every stored change (auto-save pill). */
export function useProfileForm(
  profile: Profile | undefined,
  source: ProfileSource,
  onSaved?: () => void,
): ProfileForm {
  const patch = usePatchProfile(source);
  const [values, setValues] = useState<ProfileValues | null>(null);
  const [errors, setErrors] = useState<Partial<Record<ProfileField, string>>>({});
  const [conflict, setConflict] = useState<ExtensionConflict | null>(null);
  const [failed, setFailed] = useState(false);
  const stored = useRef<ProfileValues | null>(null);
  const checkTimer = useRef<number | null>(null);
  const checkSeq = useRef(0);

  useEffect(() => {
    if (!profile) return;
    const next = valuesOf(profile);
    stored.current = next;
    setValues((current) => current ?? next);
  }, [profile]);

  useEffect(
    () => () => {
      if (checkTimer.current !== null) window.clearTimeout(checkTimer.current);
    },
    [],
  );

  const current = useMemo(() => values ?? (profile ? valuesOf(profile) : null), [values, profile]);

  const applyError = useCallback((error: unknown, fields: readonly ProfileField[]) => {
    if (error instanceof ApiError) {
      if (error.status === 409 && error.detailString('field') === 'extension') {
        setConflict({ message: error.message, suggestion: error.detailString('suggestion') });
        return;
      }
      const serverFields = error.fields;
      const mapped: Partial<Record<ProfileField, string>> = {};
      for (const field of PROFILE_FIELDS) {
        const message = serverFields[field];
        if (message) mapped[field] = message;
      }
      if (Object.keys(mapped).length > 0) {
        setErrors((before) => ({ ...before, ...mapped }));
        return;
      }
    }
    const first = fields[0];
    if (first) setErrors((before) => ({ ...before, [first]: errorMessage(error) }));
  }, []);

  const scheduleExtensionCheck = useCallback(
    (value: string) => {
      if (checkTimer.current !== null) window.clearTimeout(checkTimer.current);
      const normalized = normalizeExtension(value);
      if (normalized === null || normalized === stored.current?.extension) {
        setConflict(null);
        return;
      }
      const seq = ++checkSeq.current;
      checkTimer.current = window.setTimeout(() => {
        checkExtension(value, source)
          .then((result) => {
            if (seq !== checkSeq.current) return;
            setConflict(
              result.available || !result.message
                ? null
                : { message: result.message, suggestion: result.suggestion },
            );
          })
          .catch(() => {
            // The live hint is advisory; the save answers the conflict authoritatively.
          });
      }, 350);
    },
    [source],
  );

  const setValue = useCallback(
    (field: ProfileField, value: string) => {
      setValues((before) => (before ? { ...before, [field]: value } : before));
      setErrors((before) => {
        if (before[field] === undefined) return before;
        const next = { ...before };
        delete next[field];
        return next;
      });
      if (field === 'extension') scheduleExtensionCheck(value);
    },
    [scheduleExtensionCheck],
  );

  const changedFields = useCallback(
    (all: ProfileValues): ProfileField[] =>
      PROFILE_FIELDS.filter((field) => all[field].trim() !== (stored.current?.[field] ?? '').trim()),
    [],
  );

  const save = useCallback(
    async (fields: readonly ProfileField[], all: ProfileValues): Promise<boolean> => {
      const invalid: Partial<Record<ProfileField, string>> = {};
      for (const field of fields) {
        const message = validateField(field, all[field], source);
        if (message) invalid[field] = message;
      }
      if (Object.keys(invalid).length > 0) {
        setErrors((before) => ({ ...before, ...invalid }));
        setFailed(true);
        return false;
      }
      if (fields.length === 0) return true;
      const body: ProfilePatch = {};
      for (const field of fields) body[field] = patchValue(field, all[field]);
      try {
        const result = await patch.mutateAsync(body);
        const fresh = valuesOf(result);
        stored.current = fresh;
        setValues((before) => {
          if (!before) return fresh;
          const next = { ...before };
          for (const field of fields) next[field] = fresh[field];
          return next;
        });
        if (fields.includes('extension')) setConflict(null);
        onSaved?.();
        return true;
      } catch (error) {
        applyError(error, fields);
        setFailed(true);
        return false;
      }
    },
    [applyError, onSaved, patch, source],
  );

  const commit = useCallback(
    async (field: ProfileField) => {
      if (!current) return false;
      if (!changedFields(current).includes(field)) return true;
      const ok = await save([field], current);
      if (ok && Object.keys(errors).filter((key) => key !== field).length === 0 && conflict === null)
        setFailed(false);
      return ok;
    },
    [changedFields, conflict, current, errors, save],
  );

  const commitAll = useCallback(async () => {
    if (!current) return false;
    const ok = await save(changedFields(current), current);
    if (ok) setFailed(false);
    return ok;
  }, [changedFields, current, save]);

  const reset = useCallback(() => {
    if (stored.current) setValues(stored.current);
    setErrors({});
    setConflict(null);
    setFailed(false);
  }, []);

  const takeSuggestion = useCallback(() => {
    if (!conflict?.suggestion) return;
    setValue('extension', conflict.suggestion);
    setConflict(null);
  }, [conflict, setValue]);

  const problemCount = Object.keys(errors).length + (conflict && errors.extension === undefined ? 1 : 0);

  return {
    values: current ?? {
      firstName: '',
      lastName: '',
      jobTitle: '',
      extension: '',
      mobilePhone: '',
      location: '',
      googleMeetUrl: '',
      teamsUrl: '',
      zoomUrl: '',
    },
    errors,
    conflict,
    problemCount,
    failed,
    saving: patch.isPending,
    setValue,
    commit,
    commitAll,
    reset,
    takeSuggestion,
  };
}

const COUNT_WORDS = ['', 'ein', 'zwei', 'drei', 'vier', 'fünf', 'sechs'];

/** „Bitte korrigieren Sie drei Felder, bevor Sie fortfahren." (Nutzer 03) — the count from the errors. */
export function correctionLine(count: number): string {
  const word = COUNT_WORDS[count] ?? String(count);
  return `Bitte korrigieren Sie ${word} ${count === 1 ? 'Feld' : 'Felder'}, bevor Sie fortfahren.`;
}

export interface ProfileFieldProps {
  form: ProfileForm;
  field: ProfileField;
  source: ProfileSource;
  density: FieldDensity;
  placeholder?: string | undefined;
  suggestions?: readonly string[] | undefined;
  autoFocus?: boolean | undefined;
}

function stateLabelOf(field: ProfileField, source: ProfileSource, conflictOnly: boolean): string {
  if ((VIDEO_LINK_FIELDS as readonly ProfileField[]).includes(field)) return 'ungültig';
  if (field === 'extension' && conflictOnly) return source === 'wizard' ? 'Hinweis' : 'bereits belegt';
  if (field === 'mobilePhone' && source === 'wizard') return 'ungültig';
  if (field === 'extension') return 'ungültig';
  return 'Pflichtfeld';
}

/** One profile field wired to the form: change → state, blur → save (auto-save), the extension's conflict line. */
export function ProfileFieldInput({
  form,
  field,
  source,
  density,
  placeholder,
  suggestions,
  autoFocus,
}: ProfileFieldProps) {
  const error = form.errors[field];
  const conflict = field === 'extension' && error === undefined ? form.conflict : null;
  const tone = error ? 'error' : conflict ? (source === 'wizard' ? 'warning' : 'error') : null;
  const message: ReactNode = error ? (
    error
  ) : conflict ? (
    conflict.suggestion && source === 'wizard' ? (
      <SuggestionMessage conflict={conflict} onTake={form.takeSuggestion} />
    ) : (
      conflict.message
    )
  ) : null;
  const numeric = field === 'mobilePhone' || field === 'extension';
  const link = (VIDEO_LINK_FIELDS as readonly ProfileField[]).includes(field);
  return (
    <ProfileInput
      label={FIELD_LABELS[field]}
      stateLabel={tone ? stateLabelOf(field, source, conflict !== null) : null}
      tone={tone}
      message={message}
      density={density}
      value={form.values[field]}
      placeholder={placeholder}
      suggestions={suggestions}
      autoFocus={autoFocus}
      inputMode={
        field === 'mobilePhone' ? 'tel' : field === 'extension' ? 'numeric' : link ? 'url' : undefined
      }
      type={field === 'mobilePhone' ? 'tel' : link ? 'url' : 'text'}
      spellCheck={link ? false : undefined}
      autoComplete={
        field === 'firstName'
          ? 'given-name'
          : field === 'lastName'
            ? 'family-name'
            : field === 'mobilePhone'
              ? 'tel'
              : field === 'jobTitle'
                ? 'organization-title'
                : 'off'
      }
      className={numeric ? styles.numeric : undefined}
      onChange={(event) => form.setValue(field, event.target.value)}
      onBlur={() => void form.commit(field)}
    />
  );
}

/** „-214 ist J. Weiler zugeordnet. Vorschlag: -231." — the suggestion is one click to take. */
function SuggestionMessage({ conflict, onTake }: { conflict: ExtensionConflict; onTake: () => void }) {
  const suggestion = conflict.suggestion ?? '';
  const marker = `Vorschlag: ${suggestion}`;
  const index = conflict.message.indexOf(marker);
  if (index < 0) {
    return (
      <>
        {conflict.message}{' '}
        <button type="button" className={styles.suggestion} onClick={onTake}>
          {suggestion} übernehmen
        </button>
      </>
    );
  }
  return (
    <>
      {conflict.message.slice(0, index)}
      <button
        type="button"
        className={styles.suggestion}
        onClick={onTake}
        aria-label={`Vorschlag ${suggestion} übernehmen`}
      >
        {marker}
      </button>
      {conflict.message.slice(index + marker.length)}
    </>
  );
}
